/**
 * Shared sport metadata for the public site — the homepage sports stack
 * and each /sports/:slug page both read from this one list, so adding a
 * sport or changing its copy only has to happen in one place.
 *
 * Emoji + doubles support are kept in sync with the in-app source of truth:
 * GamePlanTab.jsx's SPORTS list (emoji) and matchFormat.js's DOUBLES_SPORTS
 * (which sports can be logged as doubles — squash is singles-only).
 *
 * Content follows the same three-level story as the homepage's
 * HierarchySection (Season → Competitions → Matches), told per sport:
 *  - Season: name/description/goal + milestones (type: result/skill/other;
 *    status: active/achieved/not_achieved, with their own rating). See
 *    docs_zenanalytics/10-SEASON-GOALS-MILESTONES-2026-09-29.md.
 *  - Competitions: optionally linked to a season (season_goal_id); reviewed
 *    after on Goal verdict + Performance / Plan outcome / Tactical
 *    execution / Mental focus.
 *  - Matches: `matchCycle` splits this into the real before/after shape —
 *    a short written plan beforehand, then a result that's rated two
 *    different ways: qualitative (free-text "Post-session notes", up to
 *    4000 characters) and quantitative (Performance ★, Plan adherence ★,
 *    and a Serve/Return/Forehand/Backhand/Net-Play breakdown — "Short
 *    game" instead of "Net play" for squash and table tennis, since
 *    neither has a net to approach; see utils/sportLabels.js).
 * Deliberately framed around what the product gives a player, not around
 * what a given sport lacks (e.g. squash's missing doubles mode never
 * leads a sentence).
 */

export const SPORTS = [
  {
    slug: 'tennis',
    value: 'tennis',
    name: 'Tennis',
    emoji: '🎾',
    doubles: true,
    tagline: 'Turn a season of tennis into a record you can actually learn from.',
    body: "Every tennis player carries a half-remembered sense of their season — who they beat, who gets the better of them, which matches slipped away. ZenAnalytics replaces that with a real one. Set a season goal — reach the quarterfinals at the club championship, build a 4-star average on serve — and break it into milestones you can tag to a tournament or track on their own. Every tournament or league becomes a competition underneath it, and every match inside carries its own plan and result.",
    stages: [
      {
        title: 'Season', color: 'accent',
        items: [
          'A season goal with a description and date range',
          'Milestones tagged as a result, a skill, or something else — tracked active, achieved or not achieved',
        ],
      },
      {
        title: 'Competitions', color: 'purple',
        items: [
          'Every tournament or league, optionally linked to the season goal',
          'Reviewed afterward: goal verdict, plus performance, plan outcome, tactical execution and mental focus',
        ],
      },
    ],
    matchCycle: {
      before: ['Opponent and level', 'A short written plan — what you\'re focusing on today'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Full set-by-set score, tiebreaks included',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    quote: '"Matches logged inside a Competition average half a point higher on plan adherence than your standalone matches — tournament stakes seem to sharpen your preparation rather than loosen it."',
  },
  {
    slug: 'padel',
    value: 'padel',
    name: 'Padel',
    emoji: '🏸',
    doubles: true,
    tagline: 'Every partnership, every tournament, building toward one season goal.',
    body: "Padel rewards chemistry as much as skill, so the record is built around pairs, not just opponents — every match logs your partner alongside the pair you're facing, because the same player plays differently next to someone else. Set a season goal, track the tournaments inside it as competitions, and let every match — whoever you're playing with — roll up into both.",
    stages: [
      {
        title: 'Season', color: 'accent',
        items: [
          'A season goal with a description and date range',
          'Milestones tagged as a result, a skill, or something else — tracked active, achieved or not achieved',
        ],
      },
      {
        title: 'Competitions', color: 'purple',
        items: [
          'Every tournament or league, optionally linked to the season goal',
          'Reviewed afterward: goal verdict, plus performance, plan outcome, tactical execution and mental focus',
        ],
      },
    ],
    matchCycle: {
      before: ['Your partner and the opposing pair', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Sets with super-tiebreak support',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    quote: '"With this partner specifically, your Competition matches average a full star higher on net play than your standalone matches — the two of you seem to lift each other under pressure."',
  },
  {
    slug: 'squash',
    value: 'squash',
    name: 'Squash',
    emoji: '🟡',
    doubles: false,
    tagline: 'A season goal, a tournament ladder, and a plan for every game you play.',
    body: "Squash runs on ladders, box leagues and tournaments as much as on single matches, so the record starts at the season: set a goal — a ladder position, a result, a skill you're sharpening — and track it through milestones. Every round or tournament becomes a competition reviewed on more than the scoreline, and every match inside it is logged game by game to 11, plan and result both.",
    stages: [
      {
        title: 'Season', color: 'accent',
        items: [
          'A season goal with a description and date range',
          'Milestones tagged as a result, a skill, or something else — tracked active, achieved or not achieved',
        ],
      },
      {
        title: 'Competitions', color: 'purple',
        items: [
          'Every ladder round, box league or tournament, optionally linked to the season goal',
          'Reviewed afterward: goal verdict, plus performance, plan outcome, tactical execution and mental focus',
        ],
      },
    ],
    matchCycle: {
      before: ['Opponent and level', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Games to 11, best of 5',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and short game, rated individually',
      ],
    },
    quote: '"Your plan adherence rating and your win rate move together across logged matches — the games you rated highest on holding your length are also your highest-rated performances."',
  },
  {
    slug: 'badminton',
    value: 'badminton',
    name: 'Badminton',
    emoji: '🏸',
    doubles: true,
    tagline: 'One season goal. Every tournament reviewed. A plan for every match, singles or doubles.',
    body: "Set a season goal — a ranking, a result, a skill worth sharpening — and break it into milestones you can tag to a tournament or track on their own. The tournaments and leagues inside the season become competitions, reviewed on more than just who won. Every match underneath, singles or doubles, carries its own plan beforehand and a full result after.",
    stages: [
      {
        title: 'Season', color: 'accent',
        items: [
          'A season goal with a description and date range',
          'Milestones tagged as a result, a skill, or something else — tracked active, achieved or not achieved',
        ],
      },
      {
        title: 'Competitions', color: 'purple',
        items: [
          'Every tournament or league, optionally linked to the season goal',
          'Reviewed afterward: goal verdict, plus performance, plan outcome, tactical execution and mental focus',
        ],
      },
    ],
    matchCycle: {
      before: ['Opponent (and partner, in doubles) and level', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Up to 3 sets to 21',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    quote: '"Your highest performance ratings this season all follow a match where a plan was written beforehand — your last two matches were logged without one."',
  },
  {
    slug: 'pickleball',
    value: 'pickleball',
    name: 'Pickleball',
    emoji: '🎾',
    doubles: true,
    tagline: 'A season goal, every tournament reviewed, and a plan for every match you play.',
    body: "Set a season goal — win a local tournament, move up a DUPR bracket, sharpen a specific shot — and break it into milestones you can tag to a tournament or track on their own. Every tournament or league becomes a competition reviewed on more than just the result, and every match, singles or doubles, carries its own plan beforehand and a full result after.",
    stages: [
      {
        title: 'Season', color: 'accent',
        items: [
          'A season goal with a description and date range',
          'Milestones tagged as a result, a skill, or something else — tracked active, achieved or not achieved',
        ],
      },
      {
        title: 'Competitions', color: 'purple',
        items: [
          'Every tournament or league, optionally linked to the season goal',
          'Reviewed afterward: goal verdict, plus performance, plan outcome, tactical execution and mental focus',
        ],
      },
    ],
    matchCycle: {
      before: ['Opponent(s) and level', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Up to 3 sets to 11',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and net play, rated individually',
      ],
    },
    quote: '"Matches logged with a written plan beforehand average a full star higher on performance than the ones logged without one — the pattern has held for six weeks."',
  },
  {
    slug: 'table-tennis',
    value: 'table_tennis',
    name: 'Table Tennis',
    emoji: '🏓',
    doubles: true,
    tagline: 'A season goal, every tournament reviewed, and enough matches logged for real patterns to emerge.',
    body: "Table tennis players rack up matches fast — more in a season than most racket sports see — which is exactly what makes a consistent record worth keeping. Set a season goal and break it into milestones you can tag to a tournament or track on their own. Leagues and tournaments become competitions reviewed on more than the result, and every match carries its own plan, turning a season's worth of scorelines into a pattern worth acting on.",
    stages: [
      {
        title: 'Season', color: 'accent',
        items: [
          'A season goal with a description and date range',
          'Milestones tagged as a result, a skill, or something else — tracked active, achieved or not achieved',
        ],
      },
      {
        title: 'Competitions', color: 'purple',
        items: [
          'Every league or tournament, optionally linked to the season goal',
          'Reviewed afterward: goal verdict, plus performance, plan outcome, tactical execution and mental focus',
        ],
      },
    ],
    matchCycle: {
      before: ['Opponent and level', 'A short written plan for the match'],
      afterQualitative: 'Post-match notes — what worked, what to fix, in your own words',
      afterQuantitative: [
        'Up to 7 sets to 11',
        'Performance ★ and plan adherence ★',
        'Serve, return, forehand, backhand and short game, rated individually',
      ],
    },
    quote: '"Across your logged matches, your performance rating against defensive-style opponents sits a full star below your average against everyone else."',
  },
];

export function getSportBySlug(slug) {
  return SPORTS.find((s) => s.slug === slug) || null;
}
